import { memo, type ReactNode } from 'react';

import classNames from 'classnames';
import isEqual from 'fast-deep-equal';

import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Loader } from '#icons/index';

import type { PagesProps } from './types/pages.props';

import { PageButton } from './button';
import { usePagesConfiguration } from './hooks/pages-configuration.hook';

import cssClasses from './pages.module.css';

/**
 * Renders a pagination navigation component with page buttons, navigation controls, and loading indicators.
 *
 * @remarks
 * - Supports showing first/last page buttons, jump-to-page, and loading states.
 * - Uses a custom hook (`usePagesConfiguration`) to manage pagination logic and state.
 * - Displays ellipsis (`...`) for skipped page ranges.
 * - Shows a loader icon on the button that triggered a loading state.
 *
 * @param props - {@link PagesProps} for configuring pagination behavior and appearance.
 * @param props.isLoading - Whether a page navigation is currently loading.
 * @param props.maxVisiblePages - Maximum number of visible page buttons.
 * @param props.showFirstLastButtons - Whether to show "First" and "Last" navigation buttons.
 * @returns A memoized pagination navigation React element, or `null` if there are no pages.
 *
 * @example
 * ```tsx
 * <Pages
 *   page={1}
 *   totalPages={10}
 *   onPageChange={handlePageChange}
 *   isLoading={loading}
 * />
 * ```
 */
const Pages = ({ isLoading = false, maxVisiblePages = 1, showFirstLastButtons = true, ...props }: PagesProps) => {
  const {
    handleFirstPageClick,
    handleLastPageClick,
    handleNextPageClick,
    handlePageClick,
    handlePreviousPageClick,
    hasNextPage,
    hasPreviousPage,
    lastClicked,
    page,
    pages,
    totalPages,
  } = usePagesConfiguration({
    maxVisiblePages,
    ...props,
  });
  let Component: ReactNode | null = null;

  if (totalPages === 0) {
    Component = null;
  } else {
    Component = (
      <nav
        aria-label="Pagination"
        className={cssClasses.pages}>
        <div className={cssClasses.buttons}>
          {showFirstLastButtons && (
            <PageButton
              aria-label="First Page"
              disabled={!hasPreviousPage}
              onClick={handleFirstPageClick}>
              <span className="flex items-center gap-2">
                {isLoading && lastClicked === 'first' && <Loader widthAuto />}
                {(!isLoading || lastClicked !== 'first') && <ChevronsLeft widthAuto />}
                First
              </span>
            </PageButton>
          )}
          <PageButton
            aria-label="Previous Page"
            disabled={!hasPreviousPage}
            onClick={handlePreviousPageClick}>
            <span className="flex items-center gap-2">
              {isLoading && lastClicked === 'previous' && <Loader widthAuto />}
              {(!isLoading || lastClicked !== 'previous') && <ChevronLeft widthAuto />}
              Previous
            </span>
          </PageButton>
          {pages.map((value, index) => {
            const isCurrent = value === page;

            if (value === '...') {
              return (
                <PageButton
                  disabled
                  // biome-ignore lint/suspicious/noArrayIndexKey: index is fine here since value can be duplicated
                  key={`ellipsis-${index}`}>
                  ...
                </PageButton>
              );
            }

            return (
              <PageButton
                aria-label={`Goto page ${value}`}
                className={classNames({ [cssClasses.active ?? '']: isCurrent })}
                key={value}
                onClick={() => {
                  handlePageClick(value);
                }}>
                {value}
              </PageButton>
            );
          })}
          <PageButton
            aria-label="Next Page"
            disabled={!hasNextPage}
            onClick={handleNextPageClick}>
            <span className="flex items-center gap-2">
              Next
              {isLoading && lastClicked === 'next' && <Loader widthAuto />}
              {(!isLoading || lastClicked !== 'next') && <ChevronRight widthAuto />}
            </span>
          </PageButton>
          {showFirstLastButtons && (
            <PageButton
              aria-label="Last Page"
              disabled={!hasNextPage}
              onClick={handleLastPageClick}>
              <span className="flex items-center gap-2">
                Last
                {isLoading && lastClicked === 'last' && <Loader widthAuto />}
                {(!isLoading || lastClicked !== 'last') && <ChevronsRight widthAuto />}
              </span>
            </PageButton>
          )}
        </div>
      </nav>
    );
  }

  return Component;
};

export default memo(Pages, (prev, next) => {
  return isEqual(prev, next);
}) as typeof Pages;
